import type { DeviceProfile, PerformanceRatings } from "../../../editor/services/performance/common/performanceRatings";
import { withUtm } from "../../../base/common/outboundLinks.js";
import { toHumanReadableBytes } from "../../../base/common/humanize.js";
import type { RendererSettings } from "../../webgl/common/rendererSettings.js";

/**
 * A snapshot of the live render/perf state for the AI report. Structural on
 * purpose — {@link IPerformanceService} values slot straight in — so the
 * formatter stays pure and unit-testable without the DI container.
 */
export interface RenderStatsSnapshot {
    fps?: number;
    /** used JS heap, bytes */
    usedMemory?: number;
    /** GPU/VRAM total, bytes */
    gpuMemory?: number;
    /** Estimated RECLAIMABLE VRAM and which textures it sits in. Travels with the
     *  total on purpose: a "good" total reads as nothing-to-do, and the AI was left
     *  guessing "consider KTX2" at scenes where we had already measured the answer. */
    gpuMemorySavings?: {
        readonly compress: number;
        readonly duplicate: number;
        readonly textures: readonly { name: string; saving: number; width?: number; height?: number; model?: string; material?: string }[];
        /** by QUANTIZING mesh attributes / narrowing indices — a different fix from
         *  texture compression, and the one Draco/meshopt do NOT do for you */
        readonly geometry?: number;
        /** by shipping FastHDR instead of EXR/HDR environments — the only saving
         *  here that shrinks the DOWNLOAD as well */
        readonly hdr?: number;
        readonly geometries?: readonly { name: string; saving: number; verts?: number; model?: string; quantizable?: readonly string[]; nonIndexed?: boolean; usedBy?: readonly string[] }[];
    };
    targetFps?: number;
    ratings?: PerformanceRatings;
    renderStats?: { drawCalls: number; triangles: number; passes: number };
    sceneTriangles?: {
        visible: number; total: number; meshes: number; materials: number; geometries: number;
        nodes?: number; maxDepth?: number; averageDepth?: number; p95Depth?: number;
        nonRenderingNodes?: number; singleChildNonRenderingNodes?: number; longestSingleChildChain?: number;
        lights?: number; shadowCastingLights?: number; shadowMapSizes?: readonly string[];
        /** by light TYPE, with how many of each cast — the type IS the cost */
        lightTypes?: Readonly<Record<string, { count: number; shadows: number; shadowMapSizes?: readonly string[] }>>;
    };
    /** Alive resources known to the resource registry. This is deliberately
     * separate from sceneTriangles: it also sees textures in node graphs and
     * loaded resources that the quick scene walk cannot enumerate reliably. */
    loadedResources?: { geometries: number; materials: number; textures: number };
    deviceProfile?: DeviceProfile;
    /** renderers on the page + whether each drew recently */
    renderers?: readonly { type: string; active: boolean; settings?: RendererSettings }[];
    /** compiled shader programs — each was a compile that stalled a frame once, and
     *  the count is how many material variants the scene really has */
    programs?: { count: number; bytes?: number };
    /** programs that FAILED to compile or link (newest first). Captured by the
     *  extension's shader-error hook — otherwise the only symptom is a black
     *  material and a console line nobody read. */
    shaderErrors?: readonly { at: number; program?: string; vertex?: string; fragment?: string }[];
    /** rolling per-second samples (oldest first) for the window min/median/max summary */
    history?: readonly { fps: number; memory: number; gpuMemory: number; drawCalls: number; triangles: number }[];
    /** timestamped events over the window (jank w/ attribution, large loads, slow interactions).
     *  `source` = a section of the offending script around the LoAF offset (already
     *  fetched by the caller, so the AI has the culprit code without a round trip). */
    events?: readonly { at: number; kind: string; label: string; magnitude: number; detail?: string; source?: string; url?: string }[];
}

interface Stat { min: number; median: number; max: number; }
function summarize(values: number[]): Stat | null {
    const xs = values.filter(Number.isFinite).sort((a, b) => a - b);
    if (!xs.length) return null;
    const mid = Math.floor(xs.length / 2);
    const median = xs.length % 2 ? xs[mid] : Math.round((xs[mid - 1] + xs[mid]) / 2);
    return { min: xs[0], median, max: xs[xs.length - 1] };
}

/** Scales the unit instead of forcing MB: a breakdown row that reads "0 MB" is
 *  worse than useless — it says the fix is pointless when it is 700 KB. */
const bytesToMB = (b: number) => toHumanReadableBytes(b, b < 1024 * 1024 * 1024 ? 1 : 2);
const rated = (rating?: string) => (rating ? ` (rating: ${rating})` : "");

/**
 * Formats the render/scene stats + device-aware ratings as a text
 * block for the performance report. Sections are omitted when their data is
 * absent (e.g. a page with no three.js renderer contributes no render stats).
 */
export function formatRenderStatsReport(s: RenderStatsSnapshot): string {
    const lines: string[] = [];

    const live: string[] = [];
    if (s.fps !== undefined) {
        const cap = s.targetFps !== undefined ? `, display cap ~${s.targetFps}` : "";
        live.push(`- **FPS:** ${s.fps}${cap}${rated(s.ratings?.fps)}`);
    }
    if (s.usedMemory !== undefined) {
        live.push(`- **Used JS heap (CPU):** ${bytesToMB(s.usedMemory)}${rated(s.ratings?.memory)}`);
    }
    if (s.gpuMemory !== undefined) {
        live.push(`- **GPU memory (VRAM, textures + geometry):** ${bytesToMB(s.gpuMemory)}${rated(s.ratings?.gpuMemory)}`);
    }
    const save = s.gpuMemorySavings;
    const reclaimable = (save?.compress ?? 0) + (save?.duplicate ?? 0);
    if (save && reclaimable > 0) {
        const share = s.gpuMemory ? ` (~${Math.round((reclaimable / s.gpuMemory) * 100)}% of it)` : "";
        const how: string[] = [];
        // with the guide: advice the AI can hand on beats advice that ends at
        // "compress your textures"
        if (save.compress > 0) how.push(`${bytesToMB(save.compress)} by compressing textures ([KTX2/basis](https://needle.click/how-to-compression))`);
        if (save.duplicate > 0) how.push(`${bytesToMB(save.duplicate)} by deduplicating textures loaded more than once`);
        live.push(`- **Reclaimable VRAM:** ~${bytesToMB(reclaimable)}${share} — ${how.join(", ")}`);
        // WHICH textures, so the advice names files instead of a general principle
        // Duplicated textures ARE the dedupe saving, so the list legitimately holds
        // several identical entries — printed as separate rows they read as a
        // formatting fault ("why is this here twice?"). Collapse with a count.
        const grouped = new Map<string, { t: (typeof save.textures)[number]; copies: number }>();
        for (const t of save.textures) {
            const key = `${t.name}|${t.width}x${t.height}|${t.model ?? ""}|${t.material ?? ""}|${t.saving}`;
            const seen = grouped.get(key);
            if (seen) seen.copies++;
            else grouped.set(key, { t, copies: 1 });
        }
        for (const { t, copies } of [...grouped.values()].slice(0, 4)) {
            const size = t.width && t.height ? `, ${t.width}x${t.height}` : "";
            const dupes = copies > 1 ? ` × ${copies} copies` : "";
            // model AND material when we have both: the file says where to fix it,
            // the material says what to look for once you are in there
            const where = [t.model ? `in ${t.model}` : "", t.material ? `used by ${t.material}` : ""].filter(Boolean);
            const used = where.length ? `, ${where.join(", ")}` : "";
            live.push(`  - ${bytesToMB(t.saving)} from \`${t.name}\`${size}${dupes}${used}`);
        }
    }
    // HDR environments: basis/KTX2 is LDR and cannot hold one, so the generic
    // "compress your textures" is unactionable for them. FastHDR is a Needle
    // format (KTX2 UASTC HDR 4x4, precomputed PMREM) and the measured difference
    // is large enough that leaving it in the general total would bury it:
    // 4K EXR 256 MB VRAM -> 12 MB, 21.2 MB download -> 7.3 MB.
    if ((save?.hdr ?? 0) > 0) {
        live.push(`- **Reclaimable VRAM in HDR environments:** ~${bytesToMB(save!.hdr!)} by shipping FastHDR`
            + ` (KTX2 UASTC HDR) instead of .exr/.hdr — ~21x less VRAM, ~3x smaller to download and`
            + ` ~10x faster to load (no PMREM at runtime). Ordinary KTX2/basis cannot hold HDR, so this`
            + ` is a different fix from texture compression. [FastHDR](https://needle.click/fasthdr)`);
    }
    // Meshes are a SEPARATE fix, and the distinction matters: Draco and meshopt
    // shrink the download and leave VRAM alone (Draco decompresses back to full
    // floats). Quantization is what frees GPU memory.
    if ((save?.geometry ?? 0) > 0) {
        live.push(`- **Reclaimable VRAM in meshes:** ~${bytesToMB(save!.geometry!)} by mesh compression`
            + ` — quantizing vertex attributes and narrowing indices.`
            + ` NOT what Draco or meshopt alone do: those shrink the download and Draco decompresses`
            + ` back to full-precision floats, so VRAM does not move.`);
        for (const g of (save!.geometries ?? []).slice(0, 4)) {
            const verts = g.verts ? `, ${g.verts.toLocaleString("en-US")} verts` : "";
            const from = g.model ? `, in ${g.model}` : "";
            const what = g.quantizable?.length ? ` — full floats: ${g.quantizable.join(", ")}` : "";
            const idx = g.nonIndexed ? " — not indexed" : "";
            const by = g.usedBy?.length ? `, drawn by ${g.usedBy.join(", ")}` : "";
            live.push(`  - ${bytesToMB(g.saving)} from \`${g.name}\`${verts}${by}${from}${what}${idx}`);
        }
    }
    if (live.length) lines.push("### Live performance (device-aware ratings)", "", ...live, "");

    // FIRST: a program that did not compile outranks every number below it — the
    // scene is rendering something other than what was authored.
    if (s.shaderErrors?.length) {
        lines.push(`### Shader errors (${s.shaderErrors.length})`, "");
        // one compiler log is enough to act on; three is a wall of GLSL
        for (const e of s.shaderErrors.slice(0, 3)) {
            const detail = [e.program, e.vertex, e.fragment].filter(Boolean).join(" — ").slice(0, 400);
            lines.push(`- ${detail || "a shader failed to compile (no log available)"}`);
        }
        if (s.shaderErrors.length > 3) lines.push(`- …and ${s.shaderErrors.length - 3} more`);
        lines.push("");
    }

    if (s.renderers?.length) lines.push(formatRenderersSection(s.renderers).trimEnd(), "");

    const r = s.renderStats;
    if (r) {
        lines.push(
            "### Renderer (per displayed frame, 1s average)",
            "",
            "| Metric | Value |",
            "| --- | --- |",
            `| Draw calls | ${r.drawCalls} |`,
            `| Render passes | ${r.passes} |`,
            `| Triangles rasterized | ${r.triangles} (every pass, not just the visible scene) |`,
            ...(s.programs ? [`| Shader programs | ${s.programs.count}${s.programs.bytes ? ` (${bytesToMB(s.programs.bytes)})` : ""} |`] : []),
            "",
        );
    }

    const t = s.sceneTriangles;
    if (t) {
        lines.push(
            "### Scene contents (geometry actually in the scene)",
            "",
            "| Metric | Value |",
            "| --- | --- |",
            `| Triangles, not hidden | ${t.visible} |`,
            `| Triangles, full scene | ${t.total} |`,
            `| Meshes | ${t.meshes} |`,
            `| Geometries | ${t.geometries} |`,
            `| Materials | ${t.materials} |`,
            ...(t.lights !== undefined ? [`| Lights | ${t.lights} |`] : []),
            // shadow casters get their own row: each one is an extra pass over the
            // scene, which is the kind of cost a triangle count never explains
            ...(t.shadowCastingLights !== undefined ? [`| Lights casting shadows | ${t.shadowCastingLights} |`] : []),
            // BY TYPE, because the type is the cost: a shadow-casting point light
            // renders the scene into a cube (six faces, six passes) where a
            // directional light renders it once.
            ...Object.entries(t.lightTypes ?? {}).map(([type, l]) => {
                // resolution sits WITH the light that pays for it — a global row
                // hid that a point light at 1024 costs six faces where a
                // directional light at 4096 costs one
                const shadows = l.shadows
                    ? `, ${l.shadows} casting shadows${l.shadowMapSizes?.length ? ` @ ${l.shadowMapSizes.join(", ")}` : ""}`
                    : "";
                return `| ${type} | ${l.count}${shadows} |`;
            }),
            "",
        );
    }

    if (t?.nodes !== undefined) {
        lines.push(
            "### Scene graph structure (runtime cost not measured)",
            "",
            "| Metric | Value |",
            "| --- | --- |",
            `| Objects | ${t.nodes} |`,
            `| Depth | ${t.averageDepth ?? 0} average, ${t.p95Depth ?? 0} p95, ${t.maxDepth ?? 0} maximum |`,
            `| Non-rendering objects | ${t.nonRenderingNodes ?? 0} |`,
            `| Non-rendering objects with one child | ${t.singleChildNonRenderingNodes ?? 0} |`,
            `| Longest such chain | ${t.longestSingleChildChain ?? 0} |`,
            "",
        );
    }

    const loaded = s.loadedResources;
    if (loaded) {
        lines.push(
            "### Loaded 3D resources (alive in memory)",
            "",
            "| Resource | Count |",
            "| --- | --- |",
            `| Geometries | ${loaded.geometries} |`,
            `| Materials | ${loaded.materials} |`,
            `| Textures | ${loaded.textures} |`,
            "",
        );
    }

    // one line, only when it is actually true of this scene
    const cubeShadows = Object.entries(s.sceneTriangles?.lightTypes ?? {})
        .filter(([type, l]) => l.shadows > 0 && /point/i.test(type))
        .reduce((sum, [, l]) => sum + l.shadows, 0);
    if (cubeShadows > 0) {
        lines.push(
            `_${cubeShadows} shadow-casting point light${cubeShadows > 1 ? "s" : ""}: each renders the scene into a CUBE — six faces, six passes — where a directional light renders it once._`,
            "",
        );
    }

    const h = s.history;
    if (h && h.length >= 2) {
        const fps = summarize(h.map((x) => x.fps));
        const mem = summarize(h.map((x) => x.memory));
        // Drop the leading zeros: a 0 sample means the resource analysis had not
        // finished its first pass, not that VRAM was empty — reported as a minimum
        // it reads as a memory drop that never happened.
        const gpuSamples = h.map((x) => x.gpuMemory);
        const gpu = summarize(gpuSamples.some(v => v > 0) ? gpuSamples.filter(v => v > 0) : gpuSamples);
        const calls = summarize(h.map((x) => x.drawCalls));
        const tris = summarize(h.map((x) => x.triangles));
        const win: string[] = [`### Last ${h.length}s`, "", "| Metric | Min | Median | Max |", "| --- | --- | --- | --- |"];
        if (fps) win.push(`| FPS | ${fps.min} | ${fps.median} | ${fps.max} |`);
        if (mem) win.push(`| JS heap | ${bytesToMB(mem.min)} | ${bytesToMB(mem.median)} | ${bytesToMB(mem.max)} |`);
        if (gpu && gpu.max > 0) win.push(`| GPU memory | ${bytesToMB(gpu.min)} | ${bytesToMB(gpu.median)} | ${bytesToMB(gpu.max)} |`);
        if (calls) win.push(`| Draw calls | ${calls.min} | ${calls.median} | ${calls.max} |`);
        if (tris) win.push(`| Triangles rasterized | ${tris.min} | ${tris.median} | ${tris.max} |`);
        if (win.length > 4) lines.push(...win, "");
    }

    const events = s.events;
    if (events && events.length) {
        const recent = [...events].sort((a, b) => b.at - a.at).slice(0, 15);
        const evLines = recent.map((e) => {
            // the label carries only the FILE NAME (cheap, no fetch); the full url
            // is what lets a reader actually open the thing
            // The attribution sometimes IS the url (no function name to show), so
            // appending it again printed the same long url twice on one line.
            const where = e.url && !(e.detail ?? "").includes(e.url) ? ` (${e.url})` : "";
            if (e.kind === "jank") return `- **Long frame:** ${e.magnitude} ms${e.detail ? ` — ${e.detail}` : ""}${where}${e.source ? `\n\n\`\`\`js\n${e.source}\n\`\`\`\n` : ""}`;
            if (e.kind === "resource") return `- **Loaded:** ${e.label} (${bytesToMB(e.magnitude)})`;
            if (e.kind === "interaction") return `- **Slow interaction:** ${e.label} (${e.magnitude} ms)`;
            return `- ${e.label}`;
        });
        lines.push(
            `### What happened in the last ${Math.max(1, Math.round((recent[0]!.at - recent[recent.length - 1]!.at) / 1000)) || 1}s`,
            "",
            "_Frames that took too long (with the script that caused them), resources that loaded, and interactions that felt slow._",
            "",
            ...evLines,
        );
        if (events.length > recent.length) lines.push(`- …and ${events.length - recent.length} more`);
        lines.push("");
    }

    const d = s.deviceProfile;
    if (d && (d.deviceMemoryGB !== undefined || d.cores !== undefined || d.mobile !== undefined || d.heapLimitBytes !== undefined)) {
        const dev: string[] = ["### Device profile (basis for the ratings)", "", "| Property | Value |", "| --- | --- |"];
        if (d.deviceMemoryGB !== undefined) dev.push(`| Device memory | ${d.deviceMemoryGB} GB |`);
        if (d.cores !== undefined) dev.push(`| CPU cores | ${d.cores} |`);
        if (d.mobile !== undefined) dev.push(`| Mobile | ${d.mobile} |`);
        if (d.heapLimitBytes !== undefined) dev.push(`| JS heap limit | ${bytesToMB(d.heapLimitBytes)} |`);
        lines.push(...dev);
    }

    // NOT "Needle Engine stats": the extension inspects any three.js page, and a
    // report that brands somebody else's scene as ours is just wrong.
    if (!lines.length) return "## Scene & render stats\n\n_No renderer detected on this page._\n";
    return "## Scene & render stats\n\n" + lines.join("\n") + "\n";
}

// #region sharing — the "Copy performance report" button

import { scoreVerdict, type PerformanceScore } from "../../../editor/services/performance/common/performanceScore";

/**
 * The score, as the report's headline.
 *
 * The number alone is not evidence; what a reader (human or AI) needs is which
 * categories were judged, which were NOT measured, and what each issue cost —
 * otherwise "82" is a mood, not a finding.
 */
export function formatScoreSection(score: PerformanceScore | null | undefined, targetLabel: string | null): string {
    if (!score || score.score === undefined) return "";
    // BLANK lines between blocks: in rendered markdown a single newline collapses,
    // so the headline and the target ran together on one line.
    const lines: string[] = [
        "## 3D performance score",
        "",
        `**${score.score} / 100 — ${scoreVerdict(score.score)}**`,
        "",
        `Measured against: ${targetLabel ?? "this device"}`,
        "",
        "| Category | Score | Weight |",
        "| --- | --- | --- |",
    ];
    for (const c of score.categories) {
        const value = c.measured ? `${c.score}` : "not measured";
        lines.push(`| ${c.label} | ${value} | ${Math.round(c.weight * 100)}% |`);
    }
    lines.push("");
    // WHY each number is what it is — the same measurements the panel's tooltips
    // show, so a reader never has to reverse-engineer a threshold
    const measured = score.categories.filter(c => c.facts?.length);
    if (measured.length) {
        lines.push("How each was measured:", "");
        for (const c of measured) lines.push(`- **${c.label}:** ${c.facts!.join("; ")}`);
        lines.push("");
    }
    if (score.issues.length) {
        lines.push("**What cost points** (worst first):", "");
        for (const i of score.issues) lines.push(`- ${i.label} (−${i.costPoints})`);
    } else {
        lines.push("Nothing over budget.");
    }
    lines.push("");
    return lines.join("\n");
}

/** "45s" / "2m 5s" — a duration a person reads without doing arithmetic. */
function formatDuration(seconds: number): string {
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${m}m ${rest}s` : `${m}m`;
}

/*
 * DISABLED 2026-08-20: the report is Pro, whole or not at all.
 *
 * A truncated report on the clipboard was the worst of both outcomes — the user
 * believes they have the thing, pastes it, and their AI answers confidently from
 * a document with the evidence cut out. Without a licence the panel now copies
 * nothing and sells instead (PerformancePanel.copyReport).
 *
 * Kept commented rather than deleted because a free PREVIEW is a plausible future
 * (a teaser that is honest about being one), and the cut-at-a-code-fence detail
 * below is easy to get wrong the second time. Its tests are disabled alongside it
 * in test/performance/reportSharing.test.ts.
 */
// export interface LimitedReport {
//     readonly text: string;
//     readonly limited: boolean;
//     readonly hiddenLines: number;
// }
//
// /**
//  * Cut a report down for the free tier.
//  *
//  * Keeps the FIRST lines (the report is ordered headline-first, so the top is the
//  * useful part), states how much was withheld inside the copied text — a reader
//  * handed a silently truncated document draws conclusions from a hole they cannot
//  * see — and closes an open code fence so the result is still valid markdown.
//  */
// export function limitReport(
//     report: string,
//     options: { maxLines: number; notice?: string },
// ): LimitedReport {
//     const all = report.split("\n");
//     if (all.length <= options.maxLines) return { text: report, limited: false, hiddenLines: 0 };
//
//     const kept = all.slice(0, options.maxLines);
//     // an odd number of fences means we cut inside a code block
//     const fences = kept.reduce((n, l) => n + (l.trim().startsWith("```") ? 1 : 0), 0);
//     if (fences % 2 === 1) kept.push("```");
//
//     const hiddenLines = all.length - options.maxLines;
//     const notice = options.notice
//         ? `${options.notice} (${hiddenLines} more lines)`
//         : `… ${hiddenLines} more lines omitted.`;
//     return { text: [...kept, "", notice].join("\n"), limited: true, hiddenLines };
// }

// #endregion

/**
 * Provenance line for a copied report: what made it, when, and for which page.
 *
 * A report that turns up in a chat or an issue tracker weeks later is otherwise
 * unattributable — and the URL is what lets whoever reads it (or their AI) go and
 * look at the actual page.
 */
export function formatReportHeader(input: { productName: string; url?: string; at: Date | number; observedSeconds?: number }): string {
    const at = typeof input.at === "number" ? new Date(input.at) : input.at;
    const when = Number.isNaN(at.getTime()) ? "" : at.toISOString().replace("T", " ").slice(0, 19) + " UTC";
    const parts = [`Generated with ${input.productName}`];
    if (when) parts.push(`at ${when}`);
    if (input.url) parts.push(`for <${input.url}>`);
    // The numbers are NOT one instant: fps/memory/draw calls are summarised over
    // the rolling window and the events cover it too. Saying so stops a reader
    // treating a 2-minute observation as a screenshot.
    const observed = input.observedSeconds && input.observedSeconds > 0
        ? `

_Observed over the last ${formatDuration(input.observedSeconds)}; single-value metrics are the latest sample._`
        : "";
    return `# Performance report\n\n_${parts.join(" ")}_${observed}\n`;
}

/**
 * The conclusion the numbers already support.
 *
 * Everything else here is evidence; this is the one line that says what it MEANS.
 * Only stated when the GPU was actually measured — inferring "cpu bound" from a
 * low frame rate alone is the guess this whole panel exists to replace.
 */
export function formatBottleneckLine(input: {
    gpuMs?: number; targetFps: number; fps?: number; drawCalls?: number;
}): string {
    if (input.gpuMs === undefined || !input.fps || input.targetFps <= 0) return "";
    const budget = 1000 / input.targetFps;
    const gpuShare = input.gpuMs / budget;
    const missingTarget = input.fps < input.targetFps * 0.9;
    if (!missingTarget) {
        return `**Read:** hitting ~${Math.round(input.fps)} fps with the GPU at ${Math.round(gpuShare * 100)}% of the frame budget — headroom on both sides.`;
    }
    if (gpuShare >= 0.6) {
        return `**Read:** GPU bound — ${input.gpuMs.toFixed(1)} ms of the ${budget.toFixed(1)} ms budget is spent on the GPU while frames land at ~${Math.round(input.fps)} fps.`;
    }
    const calls = input.drawCalls ? ` with ${input.drawCalls} draw calls per frame` : "";
    return `**Read:** CPU bound, not GPU bound — the GPU uses only ${input.gpuMs.toFixed(1)} ms of the ${budget.toFixed(1)} ms budget${calls}, yet frames land at ~${Math.round(input.fps)} fps. Look at scene-graph work, draw-call setup and script time, not shaders.`;
}

/**
 * Which renderers exist on the page and which of them actually drew recently.
 *
 * A page can hold several (a viewer plus a thumbnail renderer, WebGL beside
 * WebGPU), and every other number here belongs to whichever one is drawing —
 * so the reader needs to know which that is.
 */
export function formatRenderersSection(
    renderers: readonly { type: string; active: boolean; settings?: RendererSettings }[],
): string {
    if (!renderers.length) return "";
    const lines = ["### Renderers on the page", ""];
    for (const r of renderers) {
        lines.push(`- **${r.type}** — ${r.active ? "drawing" : "idle (did not draw recently)"}`);
        // The globals explain costs no triangle count can: MSAA at a high pixel
        // ratio is a fill-rate multiplier, soft shadows cost per shadow-casting
        // light, and an anisotropy ceiling nothing asks for is quality left unused.
        const s = r.settings;
        if (!s) continue;
        const facts: string[] = [];
        if (s.antialias !== undefined) facts.push(`antialias ${s.antialias ? "on" : "off"}${s.samples && s.samples > 1 ? ` (${s.samples}x MSAA)` : ""}`);
        else if (s.samples !== undefined) facts.push(`${s.samples}x MSAA`);
        if (s.pixelRatio !== undefined) facts.push(`pixel ratio ${s.pixelRatio}`);
        if (s.shadows) facts.push(`shadows ${s.shadows}`);
        if (s.toneMapping) facts.push(`tone mapping ${s.toneMapping}`);
        if (s.outputColorSpace) facts.push(`output ${s.outputColorSpace}`);
        if (s.maxAnisotropy !== undefined) facts.push(`max anisotropy ${s.maxAnisotropy}`);
        if (s.maxTextureSize !== undefined) facts.push(`max texture ${s.maxTextureSize}`);
        // a page with no stencil buffer cannot be doing stencil work — worth
        // stating, because its ABSENCE is otherwise indistinguishable from a
        // failed read
        if (s.depth !== undefined || s.stencil !== undefined) {
            facts.push(`buffers: ${[s.depth ? "depth" : "", s.stencil ? "stencil" : ""].filter(Boolean).join(" + ") || "colour only"}`);
        }
        if (s.powerPreference) facts.push(`power ${s.powerPreference}`);
        if (s.webgl2 !== undefined) facts.push(s.webgl2 ? "WebGL2" : "WebGL1");
        for (const fact of facts) lines.push(`  - ${fact}`);
    }
    lines.push("");
    return lines.join("\n");
}

/** Where to go next with what the report says. Always included: the fixes it
 *  recommends (compression, hosting) are the same on any three.js page, and the
 *  MCP link is how a reader wires their own AI to this editor. */
export function formatReportFooter(): string {
    return [
        "",
        "---",
        "",
        "### Where to go from here",
        "",
        // BARE urls: this report is written to be pasted into an AI, and ~60
        // characters of utm on every link is context spent on attribution we
        // cannot collect from someone else's chat window anyway.
        "- [Optimize meshes and textures](https://needle.click/how-to-compression) — compression, quantization, what actually moves VRAM",
        "- [Host and auto-optimize assets](https://cloud.needle.tools) — upload once, get compressed variants served",
        "- [Connect your AI to this scene](https://needle.click/mcp) — inspect and edit it live over MCP",
        "",
    ].join("\n");
}

/**
 * Measured GPU time — the only number here that is not inferred.
 *
 * Frame rate says the frame was slow; this says whether the GPU was the reason.
 * The share of the display's budget is the actionable part: 8 ms of a 16.7 ms
 * frame is a GPU that is half spent, whatever the fps happens to read.
 */
export function formatGpuTimingSection(
    timing: {
        backend: string; framesMeasured: number; avgMs: number; minMs: number; maxMs: number;
        avgDrawCalls: number;
        passes: readonly { label: string; avgMs: number; avgDraws: number; frames: number }[];
    } | null | undefined,
    targetFps: number,
): string {
    if (!timing || timing.framesMeasured <= 0) return "";
    const budget = targetFps > 0 ? 1000 / targetFps : 16.7;
    const share = Math.round((timing.avgMs / budget) * 100);
    const lines: string[] = [
        "### Measured GPU time",
        "",
        `Averaged over ${timing.framesMeasured} frames on ${timing.backend} (timer queries, not inferred from fps).`,
        "",
        `- **GPU per frame:** ${timing.avgMs.toFixed(1)} ms (min ${timing.minMs.toFixed(1)}, max ${timing.maxMs.toFixed(1)}) — ~${share}% of the ${budget.toFixed(1)} ms budget at ${targetFps} fps`,
        // three resets renderer.info per render() CALL, so this counts the LAST
        // call of each measured frame, not the frame. Unlabelled it sat a few lines
        // above the frame-wide count in the Renderer table and read as a
        // contradiction (267 vs 974 on the same page).
        `- **Draw calls in the last render call of each measured frame:** ${Math.round(timing.avgDrawCalls)} — the Renderer table below counts the whole frame`,
    ];
    if (timing.passes.length) {
        // Per-pass draw counts exist only where we WRAP the draw calls (WebGL).
        // The WebGPU path reads the timestamp map, which carries ms alone, so the
        // column would be all zeros — claiming those passes draw nothing.
        const hasDraws = timing.passes.some(p => p.avgDraws > 0);
        if (hasDraws) {
            lines.push("", "| Pass | GPU ms | Draws |", "| --- | --- | --- |");
            for (const p of timing.passes) lines.push(`| ${p.label} | ${p.avgMs.toFixed(2)} | ${Math.round(p.avgDraws)} |`);
        }
        else {
            lines.push("", "| Pass | GPU ms |", "| --- | --- |");
            for (const p of timing.passes) lines.push(`| ${p.label} | ${p.avgMs.toFixed(2)} |`);
            lines.push("", `_Per-pass draw counts are not recorded on ${timing.backend}._`);
        }
    }
    lines.push("");
    return lines.join("\n");
}
